import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Inbox, CheckCircle, XCircle, Loader2, Plug } from 'lucide-react';
import { toast } from 'sonner';

export default function CasellaRefertiSection() {
  const [config, setConfig] = useState(null);
  const [password, setPassword] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [test, setTest] = useState(false);

  const carica = useCallback(() => {
    api.get('/acquisizione/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const salva = async () => {
    setSalvataggio(true);
    try {
      const body = {
        attiva: config.attiva,
        imap_host: config.imap_host,
        imap_port: Number(config.imap_port) || 993,
        imap_user: config.imap_user,
        cartella_inbox: config.cartella_inbox,
        cartella_importati: config.cartella_importati,
        cartella_errori: config.cartella_errori,
        retention_giorni: Number(config.retention_giorni) || 0,
        mittenti_autorizzati: (config.mittenti_testo ?? (config.mittenti_autorizzati || []).join(', '))
          .split(',').map(s => s.trim()).filter(Boolean),
      };
      if (password) body.imap_password = password;
      const r = await api.put('/acquisizione/config', body);
      setConfig(r.data);
      setPassword('');
      toast.success('Casella referti aggiornata');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSalvataggio(false);
  };

  const provaConnessione = async () => {
    setTest(true);
    try {
      const r = await api.post('/acquisizione/test-connessione');
      toast.success(`Casella raggiungibile: ${r.data.messaggi_in_attesa} messaggi in attesa`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Connessione non riuscita'); }
    setTest(false);
  };

  if (!config) return null;
  const ultimo = config.ultimo_controllo;

  return (
    <Card data-testid="casella-referti-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Inbox className="w-5 h-5 text-slate-600" />Casella referti (acquisizione automatica)
          {config.attiva
            ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">attiva - controllo ogni 5 minuti</Badge>
            : <Badge className="bg-slate-100 text-slate-600 text-[10px]">non attiva</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500 leading-relaxed">
          I referti scansionati dalla multifunzione e inviati alla casella dedicata vengono letti, attribuiti
          all'ospite e archiviati in cartella. I messaggi elaborati vanno nella sottocartella indicata,
          quelli con problemi nella cartella errori.
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Server IMAP</Label>
            <Input className="h-9 text-xs" placeholder="imaps.aruba.it" value={config.imap_host || ''}
              onChange={e => setConfig(c => ({ ...c, imap_host: e.target.value }))} data-testid="imap-host" />
          </div>
          <div>
            <Label className="text-xs">Porta</Label>
            <Input className="h-9 text-xs" type="number" value={config.imap_port || 993}
              onChange={e => setConfig(c => ({ ...c, imap_port: e.target.value }))} data-testid="imap-port" />
          </div>
          <div>
            <Label className="text-xs">Indirizzo della casella</Label>
            <Input className="h-9 text-xs" placeholder="referti@santamariavalleverde.com" value={config.imap_user || ''}
              onChange={e => setConfig(c => ({ ...c, imap_user: e.target.value }))} data-testid="imap-user" />
          </div>
          <div>
            <Label className="text-xs">Password {config.password_impostata && <span className="text-emerald-600">(impostata)</span>}</Label>
            <Input className="h-9 text-xs" type="password" placeholder={config.password_impostata ? 'lascia vuoto per non modificarla' : ''}
              value={password} onChange={e => setPassword(e.target.value)} data-testid="imap-password" />
          </div>
          <div>
            <Label className="text-xs">Cartella di arrivo</Label>
            <Input className="h-9 text-xs" value={config.cartella_inbox || 'INBOX'}
              onChange={e => setConfig(c => ({ ...c, cartella_inbox: e.target.value }))} data-testid="cartella-inbox" />
          </div>
          <div>
            <Label className="text-xs">Cartella importati / errori</Label>
            <div className="flex gap-2">
              <Input className="h-9 text-xs" value={config.cartella_importati || 'Importati'}
                onChange={e => setConfig(c => ({ ...c, cartella_importati: e.target.value }))} data-testid="cartella-importati" />
              <Input className="h-9 text-xs" value={config.cartella_errori || 'Errori'}
                onChange={e => setConfig(c => ({ ...c, cartella_errori: e.target.value }))} data-testid="cartella-errori" />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Mittenti autorizzati (separati da virgola; vuoto = tutti)</Label>
            <Input className="h-9 text-xs" placeholder="scanner@santamariavalleverde.com, santamariavalleverde.com"
              value={config.mittenti_testo ?? (config.mittenti_autorizzati || []).join(', ')}
              onChange={e => setConfig(c => ({ ...c, mittenti_testo: e.target.value }))} data-testid="mittenti" />
          </div>
          <div>
            <Label className="text-xs">Conservazione messaggi importati (giorni, 0 = sempre)</Label>
            <Input className="h-9 text-xs" type="number" value={config.retention_giorni ?? 90}
              onChange={e => setConfig(c => ({ ...c, retention_giorni: e.target.value }))} data-testid="retention" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant={config.attiva ? 'outline' : 'default'} size="sm"
              className={`h-9 text-xs ${config.attiva ? 'text-red-600 border-red-200' : 'bg-brand hover:bg-brand-dark text-white'}`}
              onClick={() => setConfig(c => ({ ...c, attiva: !c.attiva }))} data-testid="toggle-attiva">
              {config.attiva ? 'Disattiva acquisizione' : 'Attiva acquisizione'}
            </Button>
          </div>
        </div>

        {ultimo && (
          <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 flex items-center gap-2 flex-wrap">
            {ultimo.ok ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
            <span data-testid="ultimo-controllo">
              Ultimo controllo {new Date(ultimo.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
              {ultimo.ok ? ` - ${ultimo.documenti} documenti, ${ultimo.assegnati_auto} assegnati automaticamente`
                : ` - ${ultimo.errore || 'errore'}`}
            </span>
          </div>
        )}

        <div className="flex gap-2">
          <Button size="sm" className="h-9 text-xs bg-brand hover:bg-brand-dark text-white" onClick={salva}
            disabled={salvataggio} data-testid="salva-casella">
            {salvataggio ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Salva
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={provaConnessione}
            disabled={test} data-testid="test-casella">
            {test ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />}Prova connessione
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
